import {ChevronDown} from 'lucide-react'
import {Tooltip} from '~/src/design-system/BaseComponents'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {useAppSelector} from '~/src/store/store-hooks'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {useSwitchableAgencies, useSwitchAgency} from '~/src/hooks/useSwitchAgency'
import {getRouteForRole} from '~/src/utils/routeUtils'
import {reportCatchError} from '../../../utils/errorReporter'
import {Button} from '~/src/design-system/Primitives'

const MenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownMenu = styled.div<{$open: boolean}>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  z-index: 1000;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  background: var(--white);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-sm);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--space-1) 0;
  display: ${({$open}) => ($open ? 'block' : 'none')};
`

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-2) var(--space-4);
  border: none;
  background: none;
  font-size: var(--fs-caption);
  color: var(--neutral-800);
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--neutral-100);
  }

  &:hover .itemText {
    color: var(--brand-cta);
  }
`

const StyledText = styled.span<{$selected: boolean}>`
  font-style: normal;
  font-weight: ${({$selected}) => ($selected ? 600 : 400)};
  font-size: var(--fs-caption);
  line-height: 19px;
  color: ${({$selected}) => ($selected ? 'var(--brand-cta)' : '#383838')};
`

const StyledTitleText = styled.p`
  font-weight: var(--fw-semibold);
  font-size: var(--fs-caption);
  color: #334d6e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`

interface IUserMenuProps {
  sx?: object
}

const SelectAgencyMenu = ({sx = {}}: IUserMenuProps) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const switchAgency = useSwitchAgency()
  const {loggedInAgencyId} = useAppSelector((state) => state.root)
  const {currentUser} = useCurrentUser()
  const {showError} = useGlobalSnackbar()
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // The switcher's branches (useSwitchableAgencies): a multi-agency user's agencyAssignments, else
  // the general admin's agencyNamesAndIds.
  const availableAgencies = useSwitchableAgencies()

  const selectedName = availableAgencies.find((datum) => datum.id === loggedInAgencyId)?.name

  const handleOpenUserMenu = () => {
    setOpen(true)
  }

  const handleCloseUserMenu = useCallback(() => {
    setOpen(false)
  }, [])

  const handleClick = useCallback(
    async (id: string) => {
      try {
        // Selects the branch, recomputes a multi-agency user's permissions for it and resets the
        // previous branch's caches (useSwitchAgency, shared with the invitation notifications).
        const {permissions: permissionsResult} = await switchAgency(id, {
          onSelected: () => setOpen(false),
        })

        // Clean up agency-specific URL parameters when switching agencies
        // This prevents old agency filters from persisting in the URL
        const currentLocation = window.location
        const searchParams = new URLSearchParams(currentLocation.search)
        const agencySpecificParams = ['agencyIds', 'agencyId']
        let urlChanged = false

        agencySpecificParams.forEach((param) => {
          if (searchParams.has(param)) {
            searchParams.delete(param)
            urlChanged = true
          }
        })

        // Navigate to first accessible route based on new agency's permissions
        // If permissions were updated, use them to determine the route
        // Otherwise, fall back to using the user's role
        const targetRoute = permissionsResult
          ? getRouteForRole(permissionsResult.roleName, permissionsResult.modulePermissions)
          : getRouteForRole(currentUser?.roles?.[0] || '')

        // If user is already on the target route, stay there but clean URL params
        // Otherwise, navigate to the new target route
        const currentPath = currentLocation.pathname
        if (currentPath === targetRoute) {
          // Stay on current page, just clean the URL params
          const newSearch = searchParams.toString()
          const newUrl = `${currentPath}${newSearch ? `?${newSearch}` : ''}`
          if (urlChanged || currentLocation.search !== `?${newSearch}`) {
            navigate(newUrl, {replace: true})
          }
        } else {
          // Navigate to target route
          navigate(targetRoute)
        }
      } catch (switchError) {
        reportCatchError('Failed to switch agency:', switchError)
        showError('Failed to switch agency. Please try again.')
      }
    },
    [currentUser?.roles, navigate, showError, switchAgency],
  )

  return (
    <div style={{...sx}} data-testid="select-agency-menu">
      <MenuWrapper ref={menuRef}>
        <Tooltip content="Select Agency" side="bottom">
          <Button
            variant="ghost"
            onClick={handleOpenUserMenu}
            style={{padding: 0}}
            data-testid="select-agency-button">
            <StyledTitleText>{selectedName}</StyledTitleText>
            <ChevronDown size={16} style={{color: 'var(--neutral-600)'}} />
          </Button>
        </Tooltip>
        <DropdownMenu $open={open} data-testid="select-agency-dropdown">
          {availableAgencies.map((item) => {
            return (
              <DropdownItem
                key={item.id}
                data-testid={`select-agency-item-${item.id}`}
                onClick={() => {
                  void handleClick(item.id)
                }}>
                <StyledText className="itemText" $selected={item.id === loggedInAgencyId}>
                  {item.name}
                </StyledText>
              </DropdownItem>
            )
          })}
        </DropdownMenu>
      </MenuWrapper>
    </div>
  )
}

export default SelectAgencyMenu
